// S6.5 Phase 3 — collapsed outcome pill for a resolved permission_request.
//
// Replaces the full PermissionCard once the request is allowed/denied/auto-
// allowed. Single line, founder-mode default. The ▽ expander reveals the
// raw tool input + decision metadata for developer debugging.

import { useState } from 'react';
import { Icon, type IconName } from '../atoms/Icon';
import { useTranslation } from '../../i18n';
import { humanizePermissionRequest } from '../../lib/permissions/humanize';
import type { PermissionTerminalContent } from '../../lib/permissions/types';

export interface PermissionOutcomePillProps {
  toolName: string;
  toolInput: unknown;
  terminal: PermissionTerminalContent;
}

export function PermissionOutcomePill({
  toolName,
  toolInput,
  terminal,
}: PermissionOutcomePillProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const humanized = humanizePermissionRequest({ toolName, toolInput });

  const allowed = terminal.status === 'allowed' || terminal.status === 'auto_allowed';
  const iconName: IconName = terminal.status === 'auto_allowed' ? 'ShieldCheck' : allowed ? 'Check' : 'X';
  const color = allowed ? 'text-status-success' : 'text-status-error';

  // Founder copy: "Allowed: deleted build folder" / "Denied: deleted build
  // folder" / "Auto-allowed: deleted build folder". The headline is the
  // PAST tense of what was approved (the original "Claude wants to" prefix
  // is dropped — at this point the action is no longer requested, it
  // either ran or didn't).
  const verbed = pastTense(humanized.headline);
  const prefix =
    terminal.status === 'auto_allowed'
      ? t('permissionOutcome.autoAllowed')
      : allowed
        ? t('permissionOutcome.allowed')
        : t('permissionOutcome.denied');

  return (
    <div
      className="rounded-md border border-border/50 bg-card/40"
      data-testid="permission-outcome-pill"
      data-status={terminal.status}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-2 py-1.5 text-left rounded-md hover:bg-accent focus:outline-none focus:ring-1 focus:ring-border transition-colors"
      >
        <Icon name={iconName} className={`shrink-0 ${color}`} size={14} aria-hidden="true" />
        <span className="flex-1 truncate text-body text-foreground">
          <span className={`mr-1 font-medium ${color}`}>{prefix}</span>
          {verbed}
        </span>
        <span className="text-muted-foreground/45 shrink-0" aria-hidden="true">
          {open ? <Icon name="ChevronDown" size={12} /> : <Icon name="ChevronRight" size={12} />}
        </span>
      </button>
      {open && (
        <div className="border-t border-border/50 px-2 py-2 text-xs text-muted-foreground space-y-2" /* design-discipline: kept as text-xs — developer-mode detail */>
          <div>
            <span className="text-muted-foreground/45">{t('permissionOutcome.decidedBy')} </span>
            <span className="text-foreground">{terminal.decided_by}</span>
          </div>
          {terminal.reason && (
            <div>
              <span className="text-muted-foreground/45">{t('permissionOutcome.reason')} </span>
              <span className="text-foreground break-words">{terminal.reason}</span>
            </div>
          )}
          {terminal.pattern && (
            <div>
              <span className="text-muted-foreground/45">{t('permissionOutcome.allowlistPattern')} </span>
              <code className="font-mono text-foreground break-words">{terminal.pattern}</code>
            </div>
          )}
          <div>
            <span className="text-muted-foreground/45">{t('permissionOutcome.tool')} </span>
            <span className="text-foreground">{toolName}</span>
          </div>
          <pre className="bg-background border border-border/50 rounded p-2 overflow-auto max-h-64 whitespace-pre-wrap break-words">
            {formatInput(toolInput)}
          </pre>
        </div>
      )}
    </div>
  );
}

function pastTense(headline: string): string {
  // "Claude wants to delete files" → "delete files". "Claude wants to push
  // to GitHub" → "push to GitHub". founder-first §5: short, present-imp
  // verb is fine — Santi reads "Allowed: delete files" instinctively as
  // "I allowed deleting files".
  return headline.replace(/^Claude wants to\s+/i, '');
}

function formatInput(input: unknown): string {
  try {
    return JSON.stringify(input, null, 2);
  } catch {
    return String(input);
  }
}
